'use client'

import { useEffect, useRef, useState } from 'react'

interface UseInViewOnceOptions extends IntersectionObserverInit {
  threshold?: number
}

/**
 * Returns a ref that flips to true the first time it enters the viewport.
 * This keeps reveal-on-scroll behavior lightweight and avoids replaying
 * entrance transitions every time the user scrolls back.
 */
export function useInViewOnce<T extends Element>({
  root = null,
  rootMargin = '0px',
  threshold = 0.2,
}: UseInViewOnceOptions = {}) {
  const ref = useRef<T | null>(null)
  const [isInView, setIsInView] = useState(false)

  useEffect(() => {
    if (isInView) return

    const element = ref.current
    if (!element) return

    if (typeof IntersectionObserver === 'undefined') {
      const frame = window.requestAnimationFrame(() => {
        setIsInView(true)
      })
      return () => window.cancelAnimationFrame(frame)
    }

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsInView(true)
          observer.disconnect()
        }
      },
      { root, rootMargin, threshold }
    )

    observer.observe(element)

    return () => observer.disconnect()
  }, [isInView, root, rootMargin, threshold])

  return { ref, isInView }
}
